import { useRef, useState, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { PerspectiveCamera, Text } from '@react-three/drei';
import { useControls, Leva } from 'leva';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import LensDistortionEffect from './LensDistortionEffect';
import Background from './Background';
import Trophy from './Trophy';
import Button3D from './Button3D';
import RandomTorus from './RandomTorus';
import customFont from './Cinzel-Regular.ttf';


function lerp(start, end, t) {
    return start + (end - start) * t;
}

export default function Experience() {
    const cameraRef = useRef();
    const cursor = useRef({ x: 0, y: 0 });

    const leftTextRef = useRef();
    const rightTextRef = useRef();

    const leftTextState = useRef({ position: [-55, 0, -80], scale: 1, fontSize: 25 });
    const rightTextState = useRef({ position: [55, 0, -80], scale: 1, fontSize: 25 });

    const [countdownDayAndHour, setCountdownDayAndHour] = useState("");
    const [countdownMinAndSec, setCountdownMinAndSec] = useState("");
    const [buttonClicked, setButtonClicked] = useState(false);

    const [textOpacity, setTextOpacity] = useState(0);
    const fadeInSpeed = 0.04; // Adjust for faster/slower fade
    const hasFadedIn = useRef(false); // Only fade in once

    const { size } = useThree();
    const isMobile = size.width < 768; // Adjust this value based on your design

    const handleButtonClick = () => {
        setButtonClicked(true);
        // console.log('Button clicked!');
        // You can add more functionality here, such as:
        // - Navigate to another page
        // - Show additional content
        // - Trigger animations
        
        // Example: If you want to inform parent component through a callback
        if (typeof window !== 'undefined' && window.parent) {
            try {
                window.parent.postMessage({ type: 'BUTTON_CLICKED' }, '*');
            } catch (e) {
                console.error('Failed to post message to parent', e);
            }
        }
    };

    useEffect(() => {
        const targetDate = new Date("2025-11-19T23:00:00Z");
        const interval = setInterval(() => {
            const now = new Date();
            const timeLeft = targetDate - now;

            if (timeLeft <= 0) {
                setCountdownDayAndHour("Esports");
                setCountdownMinAndSec("Awards");
                clearInterval(interval);
            } else {
                const timeUnits = {
                    days: Math.floor(timeLeft / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((timeLeft / (1000 * 60 * 60)) % 24),
                    minutes: Math.floor((timeLeft / (1000 * 60)) % 60),
                    seconds: Math.floor((timeLeft / 1000) % 60),
                };

                setCountdownDayAndHour(`${timeUnits.days}D ${timeUnits.hours}H`);
                setCountdownMinAndSec(`${timeUnits.minutes}M ${timeUnits.seconds}S`);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    // Only trigger fade-in on first load
    useEffect(() => {
        if (!hasFadedIn.current && (countdownDayAndHour || countdownMinAndSec)) {
            setTextOpacity(0);
            hasFadedIn.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countdownDayAndHour, countdownMinAndSec]);

    const {
        k, kCube, scale, dispersion, blurEnabled, blurAmount, debug,
    } = useControls('Custom Effect',{
        k: { value: -1.4, min: -2.0, max: 2.0, step: 0.01 },
        kCube: { value: 1.0, min: 0.0, max: 1.0, step: 0.01 },
        scale: { value: 1.0, min: 0.5, max: 2.0, step: 0.01 },
        dispersion: { value: 0.02, min: 0.0, max: 0.1, step: 0.001 },
        blurEnabled: true,
        blurAmount: { value: 1.5, min: 0.0, max: 3.0, step: 0.1 },
        debug: true
    });

    const {
        backgroundSpeed, backgroundIntensity, backgroundFrequency, backgroundAmplitude
    } = useControls('Background', {
        backgroundSpeed: { value: 0.1, min: 0.1, max: 5.0, step: 0.1 },
        backgroundIntensity: { value: 7, min: 0.1, max: 100.0, step: 0.1 },
        backgroundFrequency: { value: 7, min: 0, max: 20.0, step: 0.01 },
        backgroundAmplitude: { value: 0.2, min: 0, max: 1.0, step: 0.01 },
    });

    const {
        bloomIntensity, bloomThreshold, bloomSmoothing
    } = useControls('Bloom', {
        bloomIntensity: { value: 1.7, min: 0, max: 10, step: 0.1 },
        bloomThreshold: { value: 1, min: 0, max: 5, step: 0.01 },
        bloomSmoothing: { value: 0.0, min: 0, max: 1, step: 0.01 }
    });

    const handlePointerMove = (event) => {
        cursor.current.x = (event.clientX / window.innerWidth) * 2 - 1;
        cursor.current.y = -(event.clientY / window.innerHeight) * 2 + 1;
    };

    let lastFrameTime = 0; // Track the last frame time

    useFrame((state, delta) => {
        const now = state.clock.getElapsedTime();
        if (now - lastFrameTime < 1 / 30) return; // Skip frame if less than 1/30 seconds since last frame
        lastFrameTime = now;

        if (cameraRef.current) {
            cameraRef.current.position.x += (cursor.current.x * 0.2 - cameraRef.current.position.x) * 0.05;
            cameraRef.current.position.y += (cursor.current.y * 0.2 - cameraRef.current.position.y) * 0.05;
        }

        const leftTarget = isMobile ? [-22.5, 0, -80] : [-55, 0, -80];
        const rightTarget = isMobile ? [22.5, 0, -80] : [55, 0, -80];

        const textScale = isMobile ? 0.6 : 0.8;
        const textFontSize = isMobile ? 10 : 25;

        // Animate countdown left
        leftTextState.current.position = leftTextState.current.position.map((v, i) =>
            lerp(v, leftTarget[i], 0.1)
        );
        leftTextState.current.scale = lerp(leftTextState.current.scale, textScale, 0.1);
        leftTextState.current.fontSize = lerp(leftTextState.current.fontSize, textFontSize, 0.1);

        // Animate countdown right
        rightTextState.current.position = rightTextState.current.position.map((v, i) =>
            lerp(v, rightTarget[i], 0.1)
        );
        rightTextState.current.scale = lerp(rightTextState.current.scale, textScale, 0.1);
        rightTextState.current.fontSize = lerp(rightTextState.current.fontSize, textFontSize, 0.1);

        if (leftTextRef.current) {
            leftTextRef.current.position.set(...leftTextState.current.position);
            leftTextRef.current.scale.setScalar(leftTextState.current.scale);
            leftTextRef.current.fontSize = leftTextState.current.fontSize;
        }
        if (rightTextRef.current) {
            rightTextRef.current.position.set(...rightTextState.current.position);
            rightTextRef.current.scale.setScalar(rightTextState.current.scale);
            rightTextRef.current.fontSize = rightTextState.current.fontSize;
        }

        // Fade in text opacity
        if (textOpacity < 1) {
            setTextOpacity(op => Math.min(1, op + fadeInSpeed));
        }
    });

    return (
        <>
            <Leva hidden />
            <group onPointerMove={handlePointerMove}>
                <PerspectiveCamera
                    ref={cameraRef}
                    makeDefault
                    position={[0, 0, 10]}
                    fov={75}
                    near={0.1}
                    far={130}
                />
                <directionalLight position={[-2, 0.5, 1]} intensity={2.2} />
                <ambientLight intensity={0.5} />
                {/* Warm lights for gold material */}
                <pointLight position={[3, 2, 5]} intensity={1.5} color="#ffcc88" />
                <pointLight position={[-3, 2, 5]} intensity={1.5} color="#ffcc88" />

                <Background
                    backgroundSpeed={backgroundSpeed}
                    backgroundIntensity={backgroundIntensity}
                    backgroundFrequency={backgroundFrequency}
                    backgroundAmplitude={backgroundAmplitude}
                />

                <Text
                    ref={leftTextRef}
                    fontSize={leftTextState.current.fontSize}
                    font={customFont}
                    anchorX="center"
                    anchorY="middle"
                >
                    <meshStandardMaterial color="white" transparent opacity={textOpacity} />
                    {countdownDayAndHour}
                </Text>

                <Text
                    ref={rightTextRef}
                    fontSize={rightTextState.current.fontSize}
                    font={customFont}
                    anchorX="center"
                    anchorY="middle"
                >
                    <meshStandardMaterial color="white" transparent opacity={textOpacity} />
                    {countdownMinAndSec}
                </Text>

                <Trophy />

                {/* <Button3D 
                    onClick={handleButtonClick} 
                /> */}

                <RandomTorus />

                <EffectComposer>
                    {/* <LensDistortionEffect
                        k={k}
                        kCube={kCube}
                        scale={scale}
                        dispersion={dispersion}
                        blurEnabled={blurEnabled}
                        blurAmount={blurAmount}
                        debug={debug}
                    /> */}
                    <Bloom
                        luminanceThreshold={bloomThreshold}
                        mipmapBlur
                        luminanceSmoothing={bloomSmoothing}
                        intensity={bloomIntensity}
                    />
                </EffectComposer>
            </group>
        </>
    );
}
